import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, Injector, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { isEmpty, lowerCase, map, toUpper, uniqBy, upperFirst } from 'lodash';
import { UserProfileTransactionDetailsComponent } from 'src/app/pages/app-users/app-user-profile/components/user-profile-transaction-details/user-profile-transaction-details.component';
import { ListingBaseComponent } from 'src/app/shared/components/base/listing-base.component';
import {
  SERVER_URL,
  USER_TRANSACTIONS,
} from 'src/app/shared/constants/apis-list';
import {
  DEFAULT_PAGE_INDEX,
  NUMBER_RECORDS_PER_PAGE,
} from 'src/app/shared/constants/constant-list';
import {
  ReceiverInformation,
  SenderInformation,
  TransfersTransaction,
  UserTransactions,
  UserTransactionsObject,
} from 'src/app/shared/interfaces/RemiitancePlatforms';
import { formatCurrency, toQueryParams } from 'src/app/shared/utils';
import {
  IS_TRANSACTION_B2P,
  IS_TRANSACTION_CASH_IN,
  IS_TRANSACTION_CASH_OUT,
  IS_TRANSACTION_DONATION,
  IS_TRANSACTION_P2B,
  IS_TRANSACTION_P2P,
  IS_TRANSACTION_PAYMENT,
  IS_TRANSACTION_REMITTANCE,
  IS_TRANSACTION_STATUS_CREDITED,
  IS_TRANSACTION_STATUS_PENDING,
  IS_TRANSACTION_STATUS_REJECTED_CANCELLED,
  IS_TRANSACTION_STATUS_RETURNED_EXPIRED,
  IS_TRANSACTION_STATUS_RETURNED_REJECTED,
  IS_TRANSACTION_TOP_UP,
  IS_TRANSFER,
} from 'src/app/shared/utils/transaction_utils';

interface TransactionElement {
  type: string;
  remittance: string;
  status: string;
  transaction_object_id: number;
  amount: number;
  createdOn: string;
  updatedOn: string;
}

@Component({
  selector: 'app-generic-partner-user-transactions',
  templateUrl: './generic-partner-user-transactions.component.html',
  styleUrls: ['./generic-partner-user-transactions.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
})
export class GenericPartnerUserTransactionsComponent
  extends ListingBaseComponent
  implements OnInit
{
  @Input() details?: any;

  clearFilterValue: any;

  tableFilters: any[] = [];
  pageLimit: number = NUMBER_RECORDS_PER_PAGE;
  page: number = DEFAULT_PAGE_INDEX;
  users: any[] = [];

  lastPage = 0;
  totalNumberOfPages = 0;
  totalNumberOfRecords = 0;
  hasMore = false;
  sortFieldDesc = true;

  params: HttpParams = new HttpParams();

  public user: any;
  partnerName?: string;

  amountStatus?: string;
  className?: string;
  transactionName?: string;
  transactionTYPE?: string;

  TRANSACTION_TYPES = [
    { value: 'REMITTANCE', type: 'Remittance' },
    { value: 'CASH_OUT', type: 'Cash Out' },
    { value: 'PAYMENT', type: 'Payment' },
  ];

  TRANSACTION_STATUS = [
    { value: 'Completed', transaction_status: 'Completed' },
    { value: 'Received', transaction_status: 'Received' },
    { value: 'Cancelled', transaction_status: 'Cancelled' },
    { value: 'Pending', transaction_status: 'Pending' },
    { value: 'Expired', transaction_status: 'Expired' },
    { value: 'Cashed Out', transaction_status: 'Cashed Out' },
    { value: 'Accepted', transaction_status: 'Accepted' },
    { value: 'Returned', transaction_status: 'Returned' },
    { value: 'Rejected', transaction_status: 'Rejected' },
    { value: 'Failed', transaction_status: 'Failed' },
  ];

  columnsToDisplay = [
    'type',
    'remittance',
    'status',
    'transaction_object_id',
    'amount',
    'updatedOn',
    'createdOn',
  ];

  tableDef: Array<any> = [
    {
      key: 'type',
      header: 'Transaction type',
      sorting: true,
      sortColumn: 'type',
      searchable: true,
      filter: 'type',
      options: this.TRANSACTION_TYPES,
    },
    {
      key: 'remittance',
      header: 'Provider',
      sorting: false,
      sortColumn: 'remittance',
      searchable: false,
    },
    {
      key: 'status',
      header: 'Status',
      sorting: true,
      sortColumn: 'transaction_status',
      searchable: true,
      filter: 'transaction_status',
      options: this.TRANSACTION_STATUS,
    },
    {
      key: 'transaction_object_id',
      header: 'ID',
      sorting: false,
      sortColumn: 'transaction_object_id',
      searchable: false,
    },
    {
      key: 'amount',
      header: 'Amount',
      sorting: true,
      sortColumn: 'decimal_amount',
      searchable: false,
    },
    {
      key: 'updatedOn',
      header: 'Updated on',
      sorting: true,
      sortColumn: 'datetime_updated',
      searchable: false,
    },
    {
      key: 'createdOn',
      header: 'Created on',
      sorting: true,
      sortColumn: 'datetime_created',
      searchable: false,
    },
  ];

  NC_TRANSACTION_URL: any;
  WALLET_TYPE_ID?: any;
  INITIAL_FILTERS: string;

  innerDisplayedColumns = ['status', 'updatedAt'];
  columnsToDisplayWithExpand = [...this.columnsToDisplay, 'expand'];
  dataSource = new MatTableDataSource<TransactionElement>();
  expandedElement: TransactionElement | null = null;

  constructor(
    injector: Injector,
    private dialog: MatDialog,
    private datePipe: DatePipe
  ) {
    super(injector);

    this.user = this.userService.user;
    this.partnerName = lowerCase(this.user?.partner?.name);

    const CONFIG_ = this.user?.partner?.config?.cms?.sections;
    const TRANSACTIONS = CONFIG_?.transactions?.table;
    const USERS = CONFIG_?.users?.table?.columns;
    const SOURCE_URL = USERS?.id?.links?.props?.transactions;
    this.INITIAL_FILTERS = SOURCE_URL?.source?.initial_filters;

    this.NC_TRANSACTION_URL = SOURCE_URL?.source?.endpoint
      .replace('GET /api/', '')
      .trim();

    this.WALLET_TYPE_ID = TRANSACTIONS.source?.initial_filters?.wallet_type_id;
  }

  ngOnInit(): void {
    this.route.data.subscribe((data) => {
      if (data && data.data) {
        this.details = data.data;

        this.initData();
      }
    });
  }

  clearFilter() {
    this.clearFilterValue = 1;
    this.initData();
  }

  private initData() {
    if (this.tableFilters && this.tableFilters.length) {
      this.tableFilters = uniqBy(this.tableFilters, 'field');

      this.tableFilters.forEach((item, index) => {
        Object.entries(item).forEach(([key, value]) => {
          if (typeof value !== 'undefined') {
            if (this.clearFilterValue === 1) {
              this.params = this.params.set('ordering', '');
              this.clearFilterValue = 0;
            } else {
              if (item?.ordering) {
                this.params = this.params.set('ordering', String(value));
              } else {
                if (value === 'all') {
                  this.params = this.params.set(
                    item?.order
                      ? String(`ordering[${index}].${key}`)
                      : String(item?.field),
                    ''
                  );
                } else {
                  this.params = this.params.set(
                    item?.order
                      ? String(`ordering[${index}].${key}`)
                      : String(item?.field),
                    String(value)
                  );
                }
              }
            }
          }
        });
      });
    } else {
      this.params = this.params.set('ordering', '-datetime_created');
    }

    const OFFSET = this.pageLimit * this.page - this.pageLimit;

    this.params = this.params.set('limit', Number(this.pageLimit));
    this.params = this.params.set('offset', this.page > 1 ? OFFSET : 0);

    if (this.partnerName !== 'nextcare') {
      this.params = this.params.set('wallet_type_id', '6,7,8,9,10,11,12,13');
    }

    if (!!this.details?.compliance?.app_user_uuid) {
      this.getAllUserTransactions(this.details?.compliance?.app_user_uuid);
    }
  }

  getAllUserTransactions(user_code: string) {
    let FULL_URL = '';
    const DEFAULT_URL = USER_TRANSACTIONS.replace('{userCode}', user_code);

    const NC_URL =
      SERVER_URL +
      this.NC_TRANSACTION_URL.replace(
        '{{uuid}}',
        this.details?.compliance?.app_user_uuid
      );

    if (this.partnerName === 'nextcare') {
      FULL_URL = NC_URL + `/?${toQueryParams(this.INITIAL_FILTERS)}`;
    } else {
      FULL_URL = DEFAULT_URL;
    }

    this.dataService
      .fetchData({
        apiUrl: FULL_URL,
        method: 'GET',
        params: this.params,
      })
      .subscribe({
        next: (response) => {
          if (response && Array.isArray(response.results)) {
            const transactions = response.results as UserTransactions[];
            this.users = [];
            this.users = transactions?.map((item: any) => {
              const item_history = item?.transaction_object?.status_history;

              const status_history = map(item_history, (itm) => ({
                ...itm,
              }));

              return {
                ...item,
                transaction_object: item?.transaction_object,
                sender: item?.transaction_object?.sender,
                sender_id: item?.transaction_object?.sender?.app_user_id,
                sender_name:
                  item?.transaction_object?.sender?.app_user === null
                    ? item?.transaction_object?.sender?.name
                    : item?.transaction_object?.sender?.app_user
                        ?.first_name_en +
                      ' ' +
                      item?.transaction_object?.sender?.app_user?.last_name_en,
                receiver: item?.transaction_object?.receiver,
                receiver_id: item?.transaction_object?.receiver?.app_user_id,
                receiver_name:
                  item?.transaction_object?.receiver?.app_user?.first_name_en +
                  ' ' +
                  item?.transaction_object?.receiver?.app_user?.last_name_en,
                status_history,
                updatedOn: this.datePipe.transform(
                  item?.datetime_updated,
                  'medium'
                ),
                createdOn: this.datePipe.transform(
                  item?.datetime_created,
                  'medium'
                ),
                type: item.type,
                transaction_type: item.transaction_object?.type
                  ? item.transaction_object?.type
                  : '',
                loading: false,
              };
            });

            this.users = [...this.users];

            this.dataSource = new MatTableDataSource(this.users);

            const totalPages = Math.ceil(
              Number(response.count) / Number(this.pageLimit)
            );

            this.lastPage = totalPages;
            this.totalNumberOfPages = totalPages;
            this.totalNumberOfRecords = Number(response.count);
            this.hasMore = 0 <= totalPages - this.page;
          }
        },
      });
  }

  onSortChanged(sortColumn: string) {
    this.sortFieldDesc = !this.sortFieldDesc;
    const prefix = this.sortFieldDesc === true ? '-' : '';

    this.tableFilters = [
      {
        ordering: prefix + sortColumn,
      },
    ];

    this.initData();
  }

  onSearchInputChanged(elem: any, value?: string): void {
    let new_filter: any = null;

    new_filter = elem.filter === 'transaction_type' ? 'type' : elem.filter;

    Object.assign(this.tableDef, { [new_filter]: value?.toLocaleLowerCase });

    const appIndex = this.tableFilters.findIndex((i) => i.field === new_filter);
    if (appIndex === -1) {
      this.tableFilters.push({
        field: new_filter,
        search: value,
      });
    } else {
      this.tableFilters[appIndex].field = new_filter;
      this.tableFilters[appIndex].search = value;
    }

    this.page = 1;
    this.initData();
  }

  renderTransactionType(transact_type: string, transact_obj_type: string) {
    const object_type = toUpper(transact_obj_type);

    if (IS_TRANSACTION_REMITTANCE(object_type)) {
      this.transactionTYPE = 'Remittance';
    } else if (IS_TRANSACTION_CASH_OUT(object_type)) {
      this.transactionTYPE = 'Cash Out';
    } else if (IS_TRANSACTION_PAYMENT(object_type)) {
      this.transactionTYPE = 'Payment';
    } else if (IS_TRANSACTION_CASH_IN(object_type)) {
      this.transactionTYPE = 'Top up';
    } else {
      this.transactionTYPE = upperFirst(transact_type);
    }

    return this.transactionTYPE;
  }

  renderTransactionIcon(
    transact_type: string,
    transact_obj_type: string,
    status: string,
    sender: SenderInformation,
    receiver: ReceiverInformation
  ) {
    const object_type = toUpper(transact_obj_type);
    const type = toUpper(transact_type);

    if (IS_TRANSFER(type)) {
      if (
        IS_TRANSACTION_P2P(object_type) ||
        IS_TRANSACTION_TOP_UP(object_type)
      ) {
        if (IS_TRANSACTION_STATUS_RETURNED_REJECTED(toUpper(status))) {
          return './assets/images/svg/remittance-returned.svg';
        } else if (IS_TRANSACTION_STATUS_RETURNED_EXPIRED(toUpper(status))) {
          return './assets/images/svg/cashout-cancelled.svg';
        } else {
          if (this.details?.id === sender?.app_user_id) {
            return './assets/images/svg/money_sent.svg';
          } else if (this.details?.id === receiver?.app_user_id) {
            return './assets/images/svg/remittance-moved.svg';
          }
        }
      } else if (
        IS_TRANSACTION_B2P(object_type) ||
        IS_TRANSACTION_P2B(object_type)
      ) {
        return './assets/images/svg/remittance-moved.svg';
      } else if (IS_TRANSACTION_DONATION(object_type)) {
        return './assets/images/svg/heart-red.svg';
      }
    } else if (IS_TRANSACTION_CASH_OUT(type)) {
      if (IS_TRANSACTION_STATUS_REJECTED_CANCELLED(toUpper(status))) {
        return './assets/images/svg/cashout-cancelled.svg';
      } else {
        return './assets/images/svg/cashout.svg';
      }
    } else if (IS_TRANSACTION_REMITTANCE(type)) {
      if (IS_TRANSACTION_STATUS_RETURNED_REJECTED(toUpper(status))) {
        return './assets/images/svg/remittance-returned.svg';
      } else if (IS_TRANSACTION_STATUS_REJECTED_CANCELLED(toUpper(status))) {
        return './assets/images/svg/cashout-cancelled.svg';
      } else {
        return './assets/images/svg/remittance-moved.svg';
      }
    } else if (IS_TRANSACTION_CASH_IN(type)) {
      if (IS_TRANSACTION_STATUS_REJECTED_CANCELLED(toUpper(status))) {
        return './assets/images/svg/cashout-cancelled.svg';
      } else {
        return './assets/images/svg/remittance-moved.svg';
      }
    } else if (IS_TRANSACTION_PAYMENT(type)) {
      return './assets/images/svg/payment.svg';
    }
  }

  renderTransactionName(
    type: string,
    transaction_type: string,
    status: string,
    sender: SenderInformation,
    receiver: ReceiverInformation,
    transaction_object: UserTransactionsObject
  ) {
    const TRANSACTION_TYPE = toUpper(transaction_type);
    const TYPE_ = toUpper(type);

    if (IS_TRANSFER(TYPE_)) {
      if (IS_TRANSACTION_P2P(TRANSACTION_TYPE)) {
        if (this.details?.id === sender?.app_user_id) {
          this.transactionName = !isEmpty(receiver?.app_user)
            ? receiver?.app_user?.first_name_en +
              ' ' +
              receiver?.app_user?.last_name_en
            : receiver?.first_name + ' ' + receiver?.last_name;
        } else if (this.details?.id === receiver?.app_user_id) {
          // DO NOT DELETE YET
          // if (!isEmpty(sender?.app_user)) {
          //   this.transactionName =
          //     sender?.app_user?.first_name_en +
          //     ' ' +
          //     sender?.app_user?.last_name_en;
          // } else {
          //   this.transactionName = sender?.name
          //     ? sender?.name
          //     : sender.first_name + ' ' + sender.last_name;
          // }

          this.transactionName = sender?.name
            ? sender?.name
            : sender.first_name + ' ' + sender.last_name;
        }
      } else if (
        IS_TRANSACTION_B2P(TRANSACTION_TYPE) ||
        IS_TRANSACTION_P2B(TRANSACTION_TYPE) ||
        IS_TRANSACTION_TOP_UP(TRANSACTION_TYPE)
      ) {
        this.transactionName = sender?.name;
      } else if (IS_TRANSACTION_DONATION(TRANSACTION_TYPE)) {
        this.transactionName = receiver?.first_name + ' ' + receiver?.last_name;
      }
    } else if (IS_TRANSACTION_CASH_OUT(TYPE_)) {
      this.transactionName = transaction_object?.final_cash_out_branch_name;
    } else if (IS_TRANSACTION_REMITTANCE(TYPE_)) {
      this.transactionName = transaction_object?.sender_name;
    } else if (IS_TRANSACTION_CASH_IN(TYPE_)) {
      this.transactionName = transaction_object?.branch?.name;
    } else if (IS_TRANSACTION_PAYMENT(TYPE_)) {
      this.transactionName = transaction_object?.merchant_name;
    }

    return this.transactionName;
  }

  renderTransactionAmountPrefix(
    type: string,
    transaction_type: string,
    status: string,
    sender: SenderInformation,
    receiver: ReceiverInformation
  ) {
    const TRANSACTION_TYPE = toUpper(transaction_type);
    const TYPE_ = toUpper(type);

    if (IS_TRANSFER(TYPE_)) {
      if (
        IS_TRANSACTION_P2P(TRANSACTION_TYPE) ||
        IS_TRANSACTION_P2B(TRANSACTION_TYPE) ||
        IS_TRANSACTION_TOP_UP(TRANSACTION_TYPE)
      ) {
        if (
          IS_TRANSACTION_STATUS_REJECTED_CANCELLED(toUpper(status)) ||
          IS_TRANSACTION_STATUS_RETURNED_REJECTED(toUpper(status)) ||
          IS_TRANSACTION_STATUS_RETURNED_EXPIRED(toUpper(status))
        ) {
          this.amountStatus = '';
        } else {
          if (this.details?.id === sender?.app_user_id) {
            this.amountStatus = '-';
          } else if (this.details?.id === receiver?.app_user_id) {
            this.amountStatus = '+';
          }
        }
      } else if (IS_TRANSACTION_B2P(TRANSACTION_TYPE)) {
        this.amountStatus = '+';
      }
    } else if (IS_TRANSACTION_CASH_OUT(TYPE_)) {
      if (status === 'Cancelled') {
        this.amountStatus = '';
      } else {
        this.amountStatus = '-';
      }
    } else if (IS_TRANSACTION_REMITTANCE(TYPE_)) {
      if (IS_TRANSACTION_STATUS_REJECTED_CANCELLED(toUpper(status))) {
        this.amountStatus = '';
      } else {
        this.amountStatus = '+';
      }
    } else if (IS_TRANSACTION_CASH_IN(TYPE_)) {
      if (
        IS_TRANSACTION_STATUS_PENDING(toUpper(status)) ||
        IS_TRANSACTION_STATUS_REJECTED_CANCELLED(toUpper(status))
      ) {
        return '';
      } else if (IS_TRANSACTION_STATUS_CREDITED(toUpper(status))) {
        return '+';
      } else {
        return '-';
      }
    } else if (IS_TRANSACTION_PAYMENT(TYPE_)) {
      this.amountStatus = '-';
    }

    return this.amountStatus;
  }

  renderTransactionAmount(
    type: string,
    transaction_type: string,
    status: string,
    transaction_object: UserTransactionsObject
  ) {
    const TRANSACTION_TYPE = toUpper(transaction_type);
    const TYPE_ = toUpper(type);

    if (IS_TRANSACTION_REMITTANCE(TYPE_)) {
      return formatCurrency(
        Number(transaction_object?.amount),
        transaction_object.currency
      );
    } else if (IS_TRANSACTION_CASH_OUT(TYPE_)) {
      return formatCurrency(
        Number(transaction_object?.amount),
        transaction_object.currency
      );
    } else if (IS_TRANSACTION_PAYMENT(TYPE_)) {
      return formatCurrency(
        Number(transaction_object?.amount),
        transaction_object.currency
      );
    } else if (IS_TRANSACTION_CASH_IN(TYPE_)) {
      const amounts = transaction_object?.amounts;
      return formatCurrency(
        Number(amounts?.credit?.value),
        amounts?.total?.currency!
      );
    } else if (IS_TRANSFER(TYPE_)) {
      const transaction = transaction_object as TransfersTransaction;
      if (
        IS_TRANSACTION_P2P(TRANSACTION_TYPE) ||
        IS_TRANSACTION_P2B(TRANSACTION_TYPE) ||
        IS_TRANSACTION_B2P(TRANSACTION_TYPE) ||
        IS_TRANSACTION_DONATION(TRANSACTION_TYPE)
      ) {
        return formatCurrency(
          Number(transaction?.amount?.value),
          transaction?.amount.currency!
        );
      } else if (IS_TRANSACTION_TOP_UP(TRANSACTION_TYPE)) {
        const amounts = transaction_object?.amounts;
        return formatCurrency(
          Number(amounts?.total?.value),
          amounts?.total?.currency!
        );
      }
    } else {
      return 0;
    }
  }

  renderTransactionClassName(
    type: string,
    transaction_type: string,
    status: string,
    sender: SenderInformation,
    receiver: ReceiverInformation
  ) {
    const TRANSACTION_TYPE = toUpper(transaction_type);
    const TYPE_ = toUpper(type);

    if (IS_TRANSFER(TYPE_)) {
      if (
        IS_TRANSACTION_P2P(TRANSACTION_TYPE) ||
        IS_TRANSACTION_P2B(TRANSACTION_TYPE) ||
        IS_TRANSACTION_TOP_UP(TRANSACTION_TYPE)
      ) {
        if (
          IS_TRANSACTION_STATUS_REJECTED_CANCELLED(toUpper(status)) ||
          IS_TRANSACTION_STATUS_RETURNED_REJECTED(toUpper(status)) ||
          IS_TRANSACTION_STATUS_RETURNED_EXPIRED(toUpper(status))
        ) {
          this.className = '';
        } else {
          if (this.details?.id === sender?.app_user_id) {
            this.className = 'outgoing';
          } else if (this.details?.id === receiver?.app_user_id) {
            this.className = 'incoming';
          }
        }
      } else if (IS_TRANSACTION_B2P(TRANSACTION_TYPE)) {
        this.className = 'incoming';
      }
    } else if (IS_TRANSACTION_CASH_OUT(TYPE_)) {
      if (status === 'Cancelled') {
        this.className = '';
      } else {
        this.className = 'outgoing';
      }
    } else if (IS_TRANSACTION_REMITTANCE(TYPE_)) {
      if (IS_TRANSACTION_STATUS_REJECTED_CANCELLED(toUpper(status))) {
        this.className = '';
      } else {
        this.className = 'incoming';
      }
    } else if (IS_TRANSACTION_CASH_IN(TYPE_)) {
      if (IS_TRANSACTION_STATUS_REJECTED_CANCELLED(toUpper(status))) {
        this.className = '';
      } else if (
        IS_TRANSACTION_STATUS_PENDING(toUpper(status)) ||
        IS_TRANSACTION_STATUS_CREDITED(toUpper(status))
      ) {
        return 'incoming';
      } else {
        this.className = 'outgoing';
      }
    } else if (IS_TRANSACTION_PAYMENT(TYPE_)) {
      this.className = 'outgoing';
    }

    return this.className;
  }

  onViewTransaction(details: any, $event: any) {
    $event.preventDefault();
    $event.stopPropagation();
    this.dialog.open(UserProfileTransactionDetailsComponent, {
      data: {
        details,
      },
      width: '600px',
      panelClass: 'custom',
    });
  }

  onPageLimitChanged(event: number) {
    this.pageLimit = Number(event);
    this.page = DEFAULT_PAGE_INDEX;

    this.initData();
  }

  onPageChanged(event: number) {
    this.page = Number(event);

    this.initData();
  }
}
